import { AlertTriangle, CalendarRange, CheckCircle2, HelpCircle, Loader2 } from 'lucide-react';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import type { WhpAssessment } from '../../types/spray';

// The withholding period read against the blocks this spray lands on.
//
// Three outcomes, and the middle one matters as much as the other two: cleared,
// conflicting, and unverified. A block with no harvest window on record cannot
// be cleared — saying nothing there would let an unchecked block read as a safe
// one, which is the failure this whole feature exists to stop.

interface Props {
  assessment: WhpAssessment | null;
  loading?: boolean;
  /** The creator's justification, shown only when there are conflicts. */
  reason?: string;
  onReasonChange?: (value: string) => void;
  /** True when the signed-in user holds a Spray Admin seat. Changes the copy only. */
  viewerIsSprayAdmin?: boolean;
}

export function WhpHarvestCheck({
  assessment, loading, reason, onReasonChange, viewerIsSprayAdmin,
}: Props) {
  const timezone = useOrgTimezone();

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-3 text-sm text-neutral-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Checking withholding period against harvest dates…
      </div>
    );
  }

  if (!assessment) return null;

  // No WHP on the products in the tank — there is nothing to run past a date.
  if (!assessment.whp_days) return null;

  const { conflicts, unverified, whp_clear_date, whp_days, binding_product } = assessment;
  const clear = whp_clear_date ? formatLocalDateInTz(whp_clear_date, timezone) : null;

  return (
    <div className="space-y-3">
      {/* What the period actually is, and which product set it */}
      <div className="rounded-lg border border-neutral-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <CalendarRange className="h-4 w-4 text-neutral-500" />
          <p className="text-sm font-medium text-neutral-800">
            {whp_days}-day withholding period
            {binding_product && (
              <span className="font-normal text-neutral-500"> · set by {binding_product.product_name}</span>
            )}
          </p>
        </div>
        {clear && (
          <p className="text-sm text-neutral-600 mt-1">
            Earliest safe harvest after this spray: <strong>{clear}</strong>
          </p>
        )}
      </div>

      {/* Conflicts */}
      {conflicts.length > 0 && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-800">
                Withholding period runs past harvest on {conflicts.length}{' '}
                block{conflicts.length !== 1 ? 's' : ''}
              </p>

              <ul className="mt-2 space-y-2">
                {conflicts.map((c) => (
                  <li key={c.cultivar_area_id} className="text-sm text-red-700">
                    <span className="font-medium">
                      {[c.farm_name, c.block_name, c.area_name].filter(Boolean).join(' · ')}
                    </span>
                    {c.cultivar_name && (
                      <span className="text-red-600"> ({c.cultivar_name})</span>
                    )}
                    <div className="text-xs text-red-600 mt-0.5">
                      {c.severity === 'in_harvest' ? (
                        <>
                          Picking is already open (from{' '}
                          {formatLocalDateInTz(c.earliest_harvest_date, timezone)}) — fruit off
                          these trees is going to market inside the withholding period.
                        </>
                      ) : (
                        <>
                          Earliest harvest{' '}
                          {formatLocalDateInTz(c.earliest_harvest_date, timezone)} — {c.days_short}{' '}
                          day{c.days_short !== 1 ? 's' : ''} short.
                        </>
                      )}
                      {c.harvest_status === 'estimate' && ' Harvest date is an estimate.'}
                    </div>
                  </li>
                ))}
              </ul>

              {onReasonChange && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-red-800 mb-1">
                    Why is this spray being planned anyway? (required)
                  </label>
                  <textarea
                    value={reason ?? ''}
                    onChange={(e) => onReasonChange(e.target.value)}
                    rows={3}
                    placeholder="e.g. only the late rows are being sprayed; picking on this block does not start until March"
                    className="w-full rounded-md border border-red-300 px-2.5 py-1.5 text-sm bg-white"
                  />
                  <p className="text-xs text-red-700 mt-1.5">
                    {viewerIsSprayAdmin
                      ? 'The spray will be saved and held. You hold a Spray Admin seat, so you can approve it from the spray record — the decision is recorded separately from the plan.'
                      : 'The spray will be saved and held. It cannot be assigned or started until a Spray Admin approves it.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Blocks nothing is known about */}
      {unverified.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <HelpCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                {unverified.length} block{unverified.length !== 1 ? 's have' : ' has'} no harvest
                date on record
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                {unverified.map((u) => [u.block_name, u.area_name].filter(Boolean).join(' · ')).join(', ')}
              </p>
              <p className="text-xs text-amber-700 mt-1">
                These could not be checked either way. Add a harvest window on the cultivar area
                to have them included.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Everything checked, nothing to flag */}
      {conflicts.length === 0 && unverified.length === 0 && assessment.checked_area_count > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
          <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
          <p className="text-sm text-green-800">
            Clear of harvest on all {assessment.checked_area_count} target block
            {assessment.checked_area_count !== 1 ? 's' : ''}.
          </p>
        </div>
      )}
    </div>
  );
}

export default WhpHarvestCheck;
